import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { chatbotService } from '../../../../services/chatbotService';
import { ThemeState } from '../../types/chatbot.types';
import { defaultTheme, mapToApiSettings, readThemeFromApiShape } from './themeUtils';
import ThemeColorSettings from './ThemeColorSettings';
import ThemeChatSettings from './ThemeChatSettings';
import ThemePreview from './ThemePreview';

interface ThemesTabProps {
chatbot: any;
}

const ThemesTab: React.FC<ThemesTabProps> = ({ chatbot }) => {
const [theme, setTheme] = useState<ThemeState>(() => defaultTheme());
const [originalTheme, setOriginalTheme] = useState<ThemeState | null>(null);
const [isSaving, setIsSaving] = useState(false);
const [isLoadingTheme, setIsLoadingTheme] = useState(false);
const [loadError, setLoadError] = useState<string | null>(null);

const updateThemeField = (key: keyof ThemeState, value: string) => {
setTheme((t) => ({ ...t, [key]: value }));
};

useEffect(() => {
let mounted = true;
const fetchTheme = async () => {
if (!chatbot?.id) {
setLoadError('Missing chatbot id');
return;
}
setIsLoadingTheme(true);
setLoadError(null);
try {
const resp = await chatbotService.getChatbotById(encodeURIComponent(chatbot.id));
const fetched = readThemeFromApiShape((resp as any)?.data ?? resp);
if (mounted) {
if (fetched) {
setTheme(fetched);
setOriginalTheme(fetched);
} else {
setTheme(defaultTheme());
setOriginalTheme(null);
}
}
} catch (err: any) {
console.error('Failed to fetch chatbot theme', err);
if (mounted) {
setLoadError(err?.message ?? 'Failed to load theme');
setTheme(defaultTheme());
setOriginalTheme(null);
}
} finally {
if (mounted) setIsLoadingTheme(false);
}
};

fetchTheme();
return () => {
mounted = false;
};
}, [chatbot?.id]);

const reset = () => {
if (originalTheme) {
setTheme(originalTheme);
} else {
setTheme(defaultTheme());
}
};

const saveTheme = async () => {
if (!chatbot?.id) {
toast.error('Missing chatbot id — cannot save theme.');
return;
}
setIsSaving(true);
try {
const payload = mapToApiSettings(theme);
const res = await chatbotService.updateChatbotThemeSetting(
encodeURIComponent(chatbot.id),
payload
);
toast.success(res?.message || 'Theme updated successfully!');
setOriginalTheme(theme);

try {
const fresh = await chatbotService.getChatbotById(encodeURIComponent(chatbot.id));
const fetched = readThemeFromApiShape((fresh as any)?.data ?? fresh);
if (fetched) {
setTheme(fetched);
setOriginalTheme(fetched);
}
} catch (_) {}
} catch (err: any) {
console.error('Failed to save theme', err);
toast.error('Failed to save theme: ' + (err?.message ?? 'Unknown error'));
} finally {
setIsSaving(false);
}
};

const isBusy = isSaving || isLoadingTheme;

return (
<div className="p-6">
<div className="mb-4">
{isLoadingTheme ? (
<div className="text-sm text-agentic-text-secondary">Loading theme…</div>
) : loadError ? (
<div className="text-sm text-red-500">Failed to load theme: {loadError}</div>
) : (
<div className="text-sm text-agentic-text-secondary">Theme loaded from server.</div>
)}
</div>

<div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
<div className="xl:col-span-2 space-y-5 ">
<div className="border rounded-2xl p-5 bg-gray-100">
<h3 className="text-lg font-semibold text-agentic-text-primary mb-6">Theme Settings</h3>
{/* <h4 className="text-sm font-semibold text-agentic-text-primary mb-2">Colors</h4> */}
<ThemeColorSettings theme={theme} onUpdate={updateThemeField} />
<ThemeChatSettings
theme={theme}
onUpdate={updateThemeField}
onReset={reset}
onSave={saveTheme}
isSaving={isBusy}
/>
</div>
</div>

<ThemePreview theme={theme} chatbot={chatbot} />
</div>
</div>
);
};

export default ThemesTab;